import { Dispatch } from 'redux';
import { axiosInstance, request, requestConfig } from './base';
import { UploadDocResponse } from 'store/modules/models/uploadDocResponse';
import {
  CriApiResponse,
  CadastroCriApiRequest,
  GETInstrumentosFilterParams,
  HistoricoResponse,
  SolicitarLiquidacaoRequest,
  CommandResultTyped,
} from 'common/genericTypes';
import { errorMessages } from 'common/errorMessages.strings';
import { AditamentoDiferenca, AditamentoRevisionData } from 'components/aditamentoModal/aditamentoRevisionModal';

export const GETCriDetalhes = async (
  dispatch: Dispatch,
  id: string,
  stackRequest: boolean = false
): Promise<CriApiResponse> => {
  return await request(
    () => axiosInstance.get<CriApiResponse>(`${requestConfig.api}api/v1/cris/${id}`),
    dispatch,
    undefined,
    stackRequest
  );
};

export const GETCris = async (
  dispatch: Dispatch,
  params?: GETInstrumentosFilterParams,
  stackRequest: boolean = false
): Promise<Array<CriApiResponse>> => {
  return await request(
    () => axiosInstance.get<Array<CriApiResponse>>(`${requestConfig.api}api/v1/cris`, params ? { params } : undefined),
    dispatch,
    undefined,
    stackRequest
  );
};

export const GETCriByInstrumentoId = async (
  dispatch: Dispatch,
  instrumentoId: string,
  stackRequest: boolean = false
): Promise<CriApiResponse> => {
  return await request(
    () => axiosInstance.get<CriApiResponse>(`${requestConfig.api}api/v1/cris/instrumento-financeiro/${instrumentoId}`),
    dispatch,
    undefined,
    stackRequest
  );
};

export const POSTCri = async (dispatch: Dispatch, cri: CadastroCriApiRequest, stackRequest: boolean = false) => {
  return await request(
    () => axiosInstance.post<CriApiResponse>(`${requestConfig.api}api/v1/cris`, cri),
    dispatch,
    errorMessages.instrumentoFinanceiro.generic,
    stackRequest
  );
};

export const PUTCri = async (
  dispatch: Dispatch,
  criId: string,
  cri: CadastroCriApiRequest,
  stackRequest: boolean = false
) => {
  return await request(
    () => axiosInstance.put<CriApiResponse>(`${requestConfig.api}api/v1/cris/${criId}`, cri),
    dispatch,
    undefined,
    stackRequest
  );
};

export const PUTCriAditamento = async (
  dispatch: Dispatch,
  criId: string,
  cri: CadastroCriApiRequest,
  date: string,
  document: File,
  stackRequest: boolean = false
) => {
  const fileForm = new FormData();
  fileForm.append('file', document);
  fileForm.append('dataAditamento', date);
  fileForm.append('aditamento', JSON.stringify(cri));

  return await request(
    () =>
      axiosInstance.put<string>(`${requestConfig.api}api/v1/cris/aditamento/${criId}`, fileForm, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    dispatch,
    undefined,
    stackRequest
  );
};

export const PUTUploadDoc = async (
  dispatch: Dispatch,
  criId: string,
  emissionType: string,
  file: File,
  stackRequest: boolean = false
) => {
  const data = new FormData();
  data.append('file', file);
  return await request<Array<UploadDocResponse>>(
    () =>
      axiosInstance.put<Array<UploadDocResponse>>(`${requestConfig.api}api/v1/cris/${criId}/document`, data, {
        params: {
          emissionType,
        },
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    dispatch,
    undefined,
    stackRequest
  );
};

export const PutEditarCri = async (
  dispatch: Dispatch,
  criId: string,
  cri: CadastroCriApiRequest,
  justificativa: string,
  document?: File,
  stackRequest: boolean = false
) => {
  const fileForm = new FormData();

  if (document) fileForm.append('file', document);
  fileForm.append('justificativa', justificativa);

  const criStringfyed = JSON.stringify(cri);
  fileForm.append('instrumentoFinanceiroRequest', criStringfyed);

  return await request(
    () =>
      axiosInstance.put<CommandResultTyped<string>>(`${requestConfig.api}api/v1/cris/editar/${criId}`, fileForm, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    dispatch,
    undefined,
    stackRequest
  );
};

export const DELETEUploadDoc = async (
  dispatch: Dispatch,
  criId: string,
  criDocumentId: string,
  stackRequest: boolean = false
) => {
  return await request(
    () => axiosInstance.delete(`${requestConfig.api}api/v1/cris/${criId}/document/${criDocumentId}`),
    dispatch,
    undefined,
    stackRequest
  );
};

export const GETCriDocumentDownloadUrl = async (
  dispatch: Dispatch,
  criId: string,
  criDocumentId: string,
  stackRequest: boolean = false
) => {
  return await request(
    () => axiosInstance.get<string>(`${requestConfig.api}api/v1/cris/${criId}/document/${criDocumentId}/download`),
    dispatch,
    undefined,
    stackRequest
  );
};

export const POSTInterOpCri = async (dispatch: Dispatch, cri: CadastroCriApiRequest, stackRequest: boolean = false) => {
  return await request(
    () => axiosInstance.post<CriApiResponse>(`${requestConfig.api}api/v1/cris/transferencia-dvm`, cri),
    dispatch,
    undefined,
    stackRequest
  );
};

export const POSTEnviarLiquidacao = async (
  dispatch: Dispatch,
  criId: string,
  criSolicitarLiquidacaoRequest: SolicitarLiquidacaoRequest,
  stackRequest: boolean = false
) => {
  return await request(
    () =>
      axiosInstance.post<CriApiResponse>(
        `${requestConfig.api}api/v1/cris/${criId}/enviar-liquidacao`,
        criSolicitarLiquidacaoRequest
      ),
    dispatch,
    undefined,
    stackRequest
  );
};

export const POSTConfirmarLiquidacaoCri = async (dispatch: Dispatch, criId: string, precoUnitarioLiquidacao: number, stackRequest: boolean = false) => {
  return await request(
    () => axiosInstance.post<CriApiResponse>(`${requestConfig.api}api/v1/cris/${criId}/confirmar-liquidacao`,
      { precoUnitario: precoUnitarioLiquidacao },
      { headers: { 'Content-Type': 'application/json' } }),
    dispatch,
    undefined,
    stackRequest
  );
};

export const GETHistoricoCri = async (dispatch: Dispatch, criId: string, stackRequest: boolean = false) => {
  return await request(
    () =>
      axiosInstance.get<Array<HistoricoResponse>>(`${requestConfig.api}api/v1/cris/${criId}/historico-de-alteracoes`),
    dispatch,
    undefined,
    stackRequest
  );
};

export const GETAditamentoCri = async (dispatch: Dispatch, aditamentoId: string, stackRequest: boolean = false) => {
  return await request(
    () => axiosInstance.get<AditamentoRevisionData>(`${requestConfig.api}api/v1/cris/aditamento/${aditamentoId}`),
    dispatch,
    undefined,
    stackRequest
  );
};

export const PUTAditamentoCri = async (dispatch: Dispatch, aditamentoId: string, diferenca: AditamentoDiferenca[], stackRequest: boolean = false) => {
  return await request(
    () => axiosInstance.put<Array<AditamentoDiferenca>>(`${requestConfig.api}api/v1/cris/aditamento/atualizar/${aditamentoId}`, diferenca),
    dispatch,
    undefined,
    stackRequest
  );
};